/** Mieterportal 2.0 (MP-C): Termine, Neuigkeiten, Feedback, Regal-Fotos, Zugänge, Umziehen, Abholliste, Self-Service, Kalender. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { abholliste, angebotFuerMieter, neuigkeitenListe, regalFotos, terminFenster, termineListe, zugaengeListe } from "../../worker/data/mieterportal-c";

export type Angebot = Awaited<ReturnType<typeof angebotFuerMieter>>;
export type Termin = Awaited<ReturnType<typeof termineListe>>[number];
export type TerminTag = Awaited<ReturnType<typeof terminFenster>>[number];
export type Neuigkeit = Awaited<ReturnType<typeof neuigkeitenListe>>[number];
export type RegalFoto = Awaited<ReturnType<typeof regalFotos>>[number];
export type Zugang = Awaited<ReturnType<typeof zugaengeListe>>[number];
export type Abholliste = Awaited<ReturnType<typeof abholliste>>;

function nach() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["mieter"] });
}

export const useTermine = () => useQuery({ queryKey: ["mieter", "termine"], queryFn: () => rufe<{ termine: Termin[]; fenster: TerminTag[] }>(api.api.mieter.termine.$get()) });
export const useNeuigkeiten = () => useQuery({ queryKey: ["mieter", "neuigkeiten"], queryFn: () => rufe<{ neuigkeiten: Neuigkeit[] }>(api.api.mieter.neuigkeiten.$get()), staleTime: 60_000 });
export const useRegalFotos = () => useQuery({ queryKey: ["mieter", "regal-fotos"], queryFn: () => rufe<{ fotos: RegalFoto[] }>(api.api.mieter["regal-fotos"].$get()) });
export const useZugaenge = () => useQuery({ queryKey: ["mieter", "zugaenge"], queryFn: () => rufe<{ zugaenge: Zugang[]; geteilt: boolean }>(api.api.mieter.zugaenge.$get()) });
export const useAbholliste = () => useQuery({ queryKey: ["mieter", "abholliste"], queryFn: () => rufe<Abholliste>(api.api.mieter.abholliste.$get()) });
export const useAngebot = () => useQuery({ queryKey: ["mieter", "angebot"], queryFn: () => rufe<Angebot>(api.api.mieter.angebot.$get()) });
export const useKalender = () => useQuery({ queryKey: ["mieter", "kalender"], queryFn: () => rufe<{ url: string }>(api.api.mieter.kalender.$get()), staleTime: Infinity });

export function useTerminBuchen() {
  const n = nach();
  return useMutation({ mutationFn: (d: { art: "einzug" | "auszug" | "sonstiges"; tag: string; von: string; notiz: string }) => rufe<{ id: string }>(api.api.mieter.termine.$post({ json: d })), onSuccess: n });
}
export function useTerminAbsagen() {
  const n = nach();
  return useMutation({ mutationFn: (id: string) => rufe(api.api.mieter.termine[":id"].absagen.$post({ param: { id } })), onSuccess: n });
}
export function useFeedbackSenden() {
  return useMutation({ mutationFn: (d: { an: "laden" | "anbieter"; text: string }) => rufe<{ id: string }>(api.api.mieter.feedback.$post({ json: d })) });
}
export function useZugangEinladen() {
  const n = nach();
  return useMutation({ mutationFn: (d: { email: string; name: string }) => rufe(api.api.mieter.zugaenge.$post({ json: d })), onSuccess: n });
}
export function useZugangEntfernen() {
  const n = nach();
  return useMutation({ mutationFn: (id: string) => rufe(api.api.mieter.zugaenge[":id"].$delete({ param: { id } })), onSuccess: n });
}
export function useUmziehen() {
  const n = nach();
  return useMutation({ mutationFn: (d: { artikelIds: string[]; regalId: string }) => rufe<{ umgezogen: number }>(api.api.mieter.artikel.umziehen.$post({ json: d })), onSuccess: n });
}
export function useVerlaengern() {
  const n = nach();
  return useMutation({ mutationFn: (d: { id: string; dauer: number }) => rufe<{ ende: string | null }>(api.api.mieter.vertrag[":id"].verlaengern.$post({ param: { id: d.id }, json: { dauer: d.dauer, gelesen: true } })), onSuccess: n });
}
export function useKuendigen() {
  const n = nach();
  return useMutation({ mutationFn: (id: string) => rufe<{ ende: string | null }>(api.api.mieter.vertrag[":id"].kuendigen.$post({ param: { id }, json: { bestaetigt: true } })), onSuccess: n });
}
export function useBuchen() {
  const n = nach();
  return useMutation({ mutationFn: (d: { regalId: string; art: "fest" | "abo"; einheit: "woche" | "monat"; dauer?: number; start: string }) => rufe<{ id: string; nummer: string; status: string }>(api.api.mieter.buchen.$post({ json: d })), onSuccess: n });
}

/** E12.4: Wahl zum Mietende. */
export function useMietendeWahl() {
  const n = nach();
  return useMutation({ mutationFn: (d: { id: string; wahl: "verlaengern" | "abholen" | "spenden" }) => rufe(api.api.mieter.vertrag[":id"]["mietende-wahl"].$post({ param: { id: d.id }, json: { wahl: d.wahl } })), onSuccess: n });
}
